import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import subCategoryService from '../../../services/subCategoryService';
import categoryService from '../../../services/categoryService';
import '../../../pages/Pages.css';
import './SubCategories.css';
import { API_BASE_URL } from '../../../config/constants';

const SubCategories = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const [editFormData, setEditFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    image: '',
    sort_order: '',
    is_active: true
  });
  const [addFormData, setAddFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    image: '',
    sort_order: '',
    is_active: true
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadSubCategories();
    loadCategories();
  }, []);

  const loadSubCategories = async () => {
    try {
      setLoading(true);
      const response = await subCategoryService.getAllSubCategories();
      setSubCategories(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load sub-categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const activeCategories = await categoryService.getActiveCategories();
      setCategories(activeCategories);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load categories', 'error');
    }
  };

  const handleViewSubCategory = async (subCategoryId) => {
    try {
      const response = await subCategoryService.getSubCategoryById(subCategoryId);
      setSelectedSubCategory(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load sub-category details', 'error');
    }
  };

  const handleEditSubCategory = async (subCategory) => {
    setSelectedSubCategory(subCategory);
    setEditFormData({
      category_id: subCategory.category_id.toString(),
      name: subCategory.name,
      description: subCategory.description || '',
      image: subCategory.image || '',
      sort_order: subCategory.sort_order || '',
      is_active: subCategory.is_active
    });
    setImagePreview(null);
    setShowEditModal(true);
  };

  const handleUpdateSubCategory = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        ...editFormData,
        category_id: parseInt(editFormData.category_id),
        sort_order: editFormData.sort_order ? parseInt(editFormData.sort_order) : 0
      };
      
      await subCategoryService.updateSubCategory(selectedSubCategory.id, formData);
      Swal.fire('Success!', 'Sub-category updated successfully', 'success');
      setShowEditModal(false);
      loadSubCategories();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update sub-category', 'error');
    }
  };

  const handleAddSubCategory = async (e) => {
    e.preventDefault();
    
    if (!addFormData.name.trim()) {
      Swal.fire('Error!', 'Sub-category name is required', 'error');
      return;
    }

    if (!addFormData.category_id) {
      Swal.fire('Error!', 'Please select a category', 'error');
      return;
    }

    try {
      const formData = {
        ...addFormData,
        category_id: parseInt(addFormData.category_id),
        sort_order: addFormData.sort_order ? parseInt(addFormData.sort_order) : 0
      };

      await subCategoryService.createSubCategory(formData);
      Swal.fire('Success!', 'Sub-category created successfully', 'success');
      setShowAddModal(false);
      resetAddForm();
      loadSubCategories();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to create sub-category', 'error');
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      category_id: '',
      name: '',
      description: '',
      image: '',
      sort_order: '',
      is_active: true
    });
    setImagePreview(null);
  };

  const handleDeleteSubCategory = (subCategory) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete "${subCategory.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await subCategoryService.deleteSubCategory(subCategory.id);
          Swal.fire('Deleted!', 'Sub-category has been deleted.', 'success');
          loadSubCategories();
        } catch (error) {
          Swal.fire('Error!', error.message || 'Failed to delete sub-category', 'error');
        }
      }
    });
  };

  const handleStatusUpdate = (subCategory) => {
    const newStatus = !subCategory.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${action} "${subCategory.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#3085d6' : '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${action} it!`
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await subCategoryService.updateSubCategoryStatus(subCategory.id, newStatus);
          Swal.fire('Updated!', `Sub-category has been ${action}d.`, 'success');
          loadSubCategories();
        } catch (error) {
          Swal.fire('Error!', error.message || `Failed to ${action} sub-category`, 'error');
        }
      }
    });
  };

  const handleImageUpload = (e, formType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        Swal.fire('Error!', 'Please select an image file', 'error');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire('Error!', 'Image size should be less than 2MB', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        
        if (formType === 'add') {
          setAddFormData({...addFormData, image: base64String});
        } else {
          setEditFormData({...editFormData, image: base64String});
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (formType) => {
    setImagePreview(null);
    if (formType === 'add') {
      setAddFormData({...addFormData, image: ''});
    } else {
      setEditFormData({...editFormData, image: ''});
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder-image.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath;
    return `${process.env.REACT_APP_API_URL || API_BASE_URL}storage/${imagePath}`;
  };

  const filteredSubCategories = subCategories.filter(subCategory => {
    const matchesSearch = subCategory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subCategory.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || subCategory.category_id.toString() === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSubCategories = [...filteredSubCategories].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="subcat-page-wrapper">
        <div className="subcat-page-header">
          <h1 className="subcat-page-title">Sub Categories</h1>
          <p className="subcat-page-subtitle">Manage product sub-categories</p>
        </div>
        <div className="subcat-loading-state">
          <div className="subcat-loading-spinner"></div>
          <p>Loading sub-categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subcat-page-wrapper">
      <div className="subcat-page-header">
        <div className="subcat-header-content">
          <div className="subcat-header-text">
            <h1 className="subcat-page-title">Sub Categories</h1>
            <p className="subcat-page-subtitle">
              Manage your product sub-categories ({filteredSubCategories.length} of {subCategories.length} sub-categories)
            </p>
          </div>
          <div className="subcat-header-actions">
            <div className="subcat-search-box">
              <input
                type="text"
                placeholder="Search sub-categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="subcat-search-input"
              />
              <span className="subcat-search-icon">🔍</span>
            </div>
            <div className="subcat-filter-section">
              <select 
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)}
                className="subcat-filter-select"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <button className="subcat-btn subcat-btn-secondary" onClick={loadSubCategories}>
              <span className="subcat-btn-icon">🔄</span>
              Refresh
            </button>
            <button 
              className="subcat-btn subcat-btn-primary" 
              onClick={() => setShowAddModal(true)}
            >
              <span className="subcat-btn-icon">➕</span>
              Add Sub-Category
            </button>
          </div>
        </div>
      </div>

      <div className="subcat-page-content">
        {sortedSubCategories.length > 0 ? (
          <div className="subcat-table-container">
            <table className="subcat-table">
              <thead className="subcat-table-header">
                <tr>
                  <th className="subcat-table-th subcat-table-image">Image</th>
                  <th 
                    className="subcat-table-th subcat-table-name sortable"
                    onClick={() => handleSort('name')}
                  >
                    <div className="subcat-table-sort-header">
                      Name
                      <span className="subcat-sort-icon">{getSortIcon('name')}</span>
                    </div>
                  </th>
                  <th className="subcat-table-th subcat-table-category">Category</th>
                  <th className="subcat-table-th subcat-table-description">Description</th>
                  <th 
                    className="subcat-table-th subcat-table-sort-order sortable"
                    onClick={() => handleSort('sort_order')}
                  >
                    <div className="subcat-table-sort-header">
                      Sort Order
                      <span className="subcat-sort-icon">{getSortIcon('sort_order')}</span>
                    </div>
                  </th>
                  <th 
                    className="subcat-table-th subcat-table-status sortable"
                    onClick={() => handleSort('is_active')}
                  >
                    <div className="subcat-table-sort-header">
                      Status
                      <span className="subcat-sort-icon">{getSortIcon('is_active')}</span>
                    </div>
                  </th>
                  <th 
                    className="subcat-table-th subcat-table-date sortable"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="subcat-table-sort-header">
                      Created
                      <span className="subcat-sort-icon">{getSortIcon('created_at')}</span>
                    </div>
                  </th>
                  <th className="subcat-table-th subcat-table-actions">Actions</th>
                </tr>
              </thead>
              <tbody className="subcat-table-body">
                {sortedSubCategories.map(subCategory => (
                  <tr key={subCategory.id} className="subcat-table-row">
                    <td className="subcat-table-td subcat-table-image-cell">
                      <div className="subcat-table-image-wrapper">
                        <img
                          src={getImageUrl(subCategory.image)}
                          alt={subCategory.name}
                          className="subcat-table-img"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </div>
                    </td>
                    <td className="subcat-table-td subcat-table-name-cell">
                      <div className="subcat-table-name-content">
                        <span className="subcat-table-name">{subCategory.name}</span>
                        <span className="subcat-table-slug">{subCategory.slug}</span>
                      </div>
                    </td>
                    <td className="subcat-table-td subcat-table-category-cell">
                      <span className="subcat-category-badge">
                        {subCategory.category?.name}
                      </span>
                    </td>
                    <td className="subcat-table-td subcat-table-description-cell">
                      <div className="subcat-table-description">
                        {subCategory.description || (
                          <span className="subcat-no-description">No description</span>
                        )}
                      </div>
                    </td>
                    <td className="subcat-table-td subcat-table-sort-order-cell">
                      <span className="subcat-sort-order-badge">{subCategory.sort_order || 0}</span>
                    </td>
                    <td className="subcat-table-td subcat-table-status-cell">
                      <div className="subcat-status-container">
                        <span className={`subcat-status-badge ${subCategory.is_active ? 'subcat-status-active' : 'subcat-status-inactive'}`}>
                          {subCategory.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="subcat-table-td subcat-table-date-cell">
                      {formatDate(subCategory.created_at)}
                    </td>
                    <td className="subcat-table-td subcat-table-actions-cell">
                      <div className="subcat-table-actions">
                        <button
                          className="subcat-table-btn subcat-table-btn-view"
                          onClick={() => handleViewSubCategory(subCategory.id)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          className="subcat-table-btn subcat-table-btn-edit"
                          onClick={() => handleEditSubCategory(subCategory)}
                          title="Edit Sub-Category"
                        >
                          ✏️
                        </button>
                        <button
                          className={`subcat-table-btn ${subCategory.is_active ? 'subcat-table-btn-deactivate' : 'subcat-table-btn-activate'}`}
                          onClick={() => handleStatusUpdate(subCategory)}
                          title={subCategory.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {subCategory.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          className="subcat-table-btn subcat-table-btn-delete"
                          onClick={() => handleDeleteSubCategory(subCategory)}
                          title="Delete Sub-Category"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="subcat-empty-state">
            <div className="subcat-empty-icon">📂</div>
            <h3 className="subcat-empty-title">No Sub-Categories Found</h3>
            <p className="subcat-empty-description">
              {searchTerm || filterCategory ? 'No sub-categories match your search criteria. Try different keywords or filters.' : 'Get started by creating your first sub-category.'}
            </p>
            <button
              className="subcat-btn subcat-btn-primary subcat-empty-action"
              onClick={() => setShowAddModal(true)}
            >
              <span className="subcat-btn-icon">➕</span>
              Add New Sub-Category
            </button>
          </div>
        )}
      </div>

      {/* View Sub-Category Modal */}
      {showViewModal && selectedSubCategory && (
        <div className="subcat-modal-overlay">
          <div className="subcat-modal subcat-modal-large">
            <div className="subcat-modal-header">
              <h3 className="subcat-modal-title">Sub-Category Details</h3>
              <button className="subcat-modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="subcat-modal-body">
              <div className="subcat-detail-view">
                <div className="subcat-detail-image-section">
                  <img 
                    src={getImageUrl(selectedSubCategory.image)} 
                    alt={selectedSubCategory.name}
                    className="subcat-detail-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg';
                    }}
                  />
                </div>
                <div className="subcat-detail-grid">
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Name</label>
                    <span className="subcat-detail-value">{selectedSubCategory.name}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Category</label>
                    <span className="subcat-detail-value">{selectedSubCategory.category?.name}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Slug</label>
                    <span className="subcat-detail-value subcat-detail-slug">{selectedSubCategory.slug}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Description</label>
                    <span className="subcat-detail-value">{selectedSubCategory.description || 'No description'}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Sort Order</label>
                    <span className="subcat-detail-value">{selectedSubCategory.sort_order}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Status</label>
                    <span className={`subcat-status-badge ${selectedSubCategory.is_active ? 'subcat-status-active' : 'subcat-status-inactive'}`}>
                      {selectedSubCategory.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Created</label>
                    <span className="subcat-detail-value">{formatDate(selectedSubCategory.created_at)}</span>
                  </div>
                  <div className="subcat-detail-item">
                    <label className="subcat-detail-label">Updated</label>
                    <span className="subcat-detail-value">{formatDate(selectedSubCategory.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="subcat-modal-footer">
              <button className="subcat-btn subcat-btn-secondary" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sub-Category Modal */}
      {showEditModal && (
        <div className="subcat-modal-overlay">
          <div className="subcat-modal subcat-modal-large">
            <div className="subcat-modal-header">
              <h3 className="subcat-modal-title">Edit Sub-Category</h3>
              <button className="subcat-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateSubCategory}>
              <div className="subcat-modal-body">
                <div className="subcat-form-grid">
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Category *</label>
                    <select
                      value={editFormData.category_id}
                      onChange={(e) => setEditFormData({...editFormData, category_id: e.target.value})}
                      className="subcat-form-select"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Sub-Category Name *</label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      className="subcat-form-input"
                      required
                    />
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Sort Order</label>
                    <input
                      type="number"
                      value={editFormData.sort_order}
                      onChange={(e) => setEditFormData({...editFormData, sort_order: e.target.value})}
                      className="subcat-form-input"
                      min="0"
                    />
                  </div>
                  <div className="subcat-form-group subcat-form-full">
                    <label className="subcat-form-label">Description</label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                      className="subcat-form-textarea"
                      rows="3"
                      placeholder="Enter sub-category description..."
                    />
                  </div>
                  <div className="subcat-form-group subcat-form-full">
                    <label className="subcat-form-label">Image</label>
                    <div className="subcat-image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'edit')}
                        className="subcat-image-input"
                      />
                      <div className="subcat-image-preview-container">
                        {(imagePreview || editFormData.image) && (
                          <div className="subcat-image-preview">
                            <img 
                              src={imagePreview || getImageUrl(editFormData.image)} 
                              alt="Preview" 
                            />
                            <button 
                              type="button" 
                              className="subcat-btn-remove-image"
                              onClick={() => removeImage('edit')}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Status</label>
                    <div className="subcat-checkbox-wrapper">
                      <label className="subcat-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editFormData.is_active}
                          onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                          className="subcat-checkbox-input"
                        />
                        <span className="subcat-checkbox-custom"></span>
                        Active Sub-Category
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="subcat-modal-footer">
                <button type="button" className="subcat-btn subcat-btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="subcat-btn subcat-btn-primary">
                  Update Sub-Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Sub-Category Modal */}
      {showAddModal && (
        <div className="subcat-modal-overlay">
          <div className="subcat-modal subcat-modal-large">
            <div className="subcat-modal-header">
              <h3 className="subcat-modal-title">Add New Sub-Category</h3>
              <button className="subcat-modal-close" onClick={() => {setShowAddModal(false); resetAddForm();}}>×</button>
            </div>
            <form onSubmit={handleAddSubCategory}>
              <div className="subcat-modal-body">
                <div className="subcat-form-grid">
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Category *</label>
                    <select
                      value={addFormData.category_id}
                      onChange={(e) => setAddFormData({...addFormData, category_id: e.target.value})}
                      className="subcat-form-select"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Sub-Category Name *</label>
                    <input
                      type="text"
                      value={addFormData.name}
                      onChange={(e) => setAddFormData({...addFormData, name: e.target.value})}
                      className="subcat-form-input"
                      required
                      placeholder="Enter sub-category name"
                    />
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Sort Order</label>
                    <input
                      type="number"
                      value={addFormData.sort_order}
                      onChange={(e) => setAddFormData({...addFormData, sort_order: e.target.value})}
                      className="subcat-form-input"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                  <div className="subcat-form-group subcat-form-full">
                    <label className="subcat-form-label">Description</label>
                    <textarea
                      value={addFormData.description}
                      onChange={(e) => setAddFormData({...addFormData, description: e.target.value})}
                      className="subcat-form-textarea"
                      rows="3"
                      placeholder="Enter sub-category description..."
                    />
                  </div>
                  <div className="subcat-form-group subcat-form-full">
                    <label className="subcat-form-label">Image</label>
                    <div className="subcat-image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'add')}
                        className="subcat-image-input"
                      />
                      <div className="subcat-image-preview-container">
                        {imagePreview && (
                          <div className="subcat-image-preview">
                            <img src={imagePreview} alt="Preview" />
                            <button 
                              type="button" 
                              className="subcat-btn-remove-image"
                              onClick={() => removeImage('add')}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="subcat-form-group">
                    <label className="subcat-form-label">Status</label>
                    <div className="subcat-checkbox-wrapper">
                      <label className="subcat-checkbox-label">
                        <input
                          type="checkbox"
                          checked={addFormData.is_active}
                          onChange={(e) => setAddFormData({...addFormData, is_active: e.target.checked})}
                          className="subcat-checkbox-input"
                        />
                        <span className="subcat-checkbox-custom"></span>
                        Active Sub-Category
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="subcat-modal-footer">
                <button type="button" className="subcat-btn subcat-btn-secondary" onClick={() => {setShowAddModal(false); resetAddForm();}}>
                  Cancel
                </button>
                <button type="submit" className="subcat-btn subcat-btn-primary">
                  Create Sub-Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategories;